'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Eye } from 'lucide-react';
import type { Prompt } from '@/data/prompts';

interface PromptCardProps {
  prompt: Prompt;
  variant?: 'default' | 'compact' | 'featured';
}

export default function PromptCard({ prompt, variant = 'default' }: PromptCardProps) {
  const promptHref = `/prompt/${prompt.slug}`;

  if (variant === 'compact') {
    return (
      <div className="card-base group overflow-hidden flex gap-3 p-3">
        <div className="relative w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
          <AppImage
            src={prompt.image}
            alt={`AI image prompt preview for ${prompt.title}`}
            fill
            sizes="80px"
            className="object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <Link
              href={promptHref}
              className="font-semibold text-sm text-foreground hover:text-primary transition-colors line-clamp-2 block"
            >
              {prompt.title}
            </Link>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{prompt.style}</p>
          </div>
          <Link
            href={promptHref}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 bg-primary text-white hover:bg-primary-dark active:scale-95 w-fit"
            aria-label={`Open prompt page for ${prompt.title}`}
          >
            <Eye size={12} />
            Open Prompt
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="card-base group overflow-hidden flex flex-col">
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <AppImage
          src={prompt.image}
          alt={`AI generated image preview for prompt: ${prompt.title} — ${prompt.style} style`}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
        {/* Style badge */}
        <div className="absolute top-3 left-3">
          <span className="badge-pink text-xs">{prompt.style}</span>
        </div>
        {/* Trending badge */}
        {prompt.trending && (
          <div className="absolute top-3 right-3">
            <span className="bg-primary text-white text-xs font-semibold px-2 py-1 rounded-full">
              Trending
            </span>
          </div>
        )}
        {/* Quick open on hover */}
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Link
            href={promptHref}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 bg-muted text-muted-foreground hover:bg-primary hover:text-white active:scale-95"
            aria-label={`Open prompt page for ${prompt.title}`}
            title="Open prompt page"
          >
            <Eye size={14} />
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex-1">
          <Link
            href={promptHref}
            className="font-semibold text-foreground hover:text-primary transition-colors line-clamp-2 text-sm leading-snug block"
          >
            {prompt.title}
          </Link>
          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2 leading-relaxed">
            {prompt.shortDescription}
          </p>
        </div>

        {/* Category tags */}
        <div className="flex flex-wrap gap-1.5">
          {prompt.categorySlugs.slice(0, 2).map((slug) => (
            <Link
              key={`card-cat-${prompt.id}-${slug}`}
              href={`/categories/${slug}`}
              className="badge-muted text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              {slug}
            </Link>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1 border-t border-border">
          <Link
            href={promptHref}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all duration-200 bg-primary text-white hover:bg-primary-dark active:scale-95 shadow-sm hover:shadow-md text-xs"
            aria-label={`Open prompt page for ${prompt.title}`}
          >
            <Eye size={14} />
            Open Prompt
          </Link>
          <Link
            href={promptHref}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-medium text-muted-foreground hover:border-primary hover:text-primary transition-all"
            aria-label={`View full details for ${prompt.title}`}
          >
            <Eye size={12} />
            View
          </Link>
        </div>
      </div>
    </article>
  );
}
